import moment, { Moment } from 'moment-timezone';
import { getClientTimezone } from 'helpers/push';
import { EventTimezoneOption } from 'graphql/types';

export const EVENT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const getDefaultEventTimezone = (): string => getClientTimezone();

export const formatEventTimezoneOptionLabel = (option: Pick<EventTimezoneOption, 'label' | 'abbreviation' | 'offset'>): string => {
  const suffix = "UTC" + (option.offset || option.abbreviation);
  return suffix ? `${option.label} — ${suffix}` : option.label;
};

export const mapEventTimezoneOptions = (
  options: Array<Pick<EventTimezoneOption, 'value' | 'label' | 'abbreviation' | 'offset'>>,
  clientTimezone?: string,
) => {
  const mapped = options.map((option) => ({
    value: option.value,
    label: formatEventTimezoneOptionLabel(option),
  }));

  if (clientTimezone && !mapped.some((option) => option.value === clientTimezone)) {
    return [
      { value: clientTimezone, label: `Your device (${clientTimezone})` },
      ...mapped,
    ];
  }

  return mapped;
};

export const formatEventDateTimeInTimezone = (value: Moment | string, timezone: string): string => {
  const wallClock = moment.isMoment(value) ? value.format(EVENT_DATETIME_FORMAT) : moment(value).format(EVENT_DATETIME_FORMAT);
  return moment.tz(wallClock, EVENT_DATETIME_FORMAT, timezone).format();
};

export const parseEventDateTimeForForm = (
  value: string | null | undefined,
  timezone?: string | null,
): Moment => {
  if (!value) {
    return moment();
  }

  if (timezone) {
    return moment.tz(value, timezone);
  }

  return moment(value);
};

export const formatTimezoneAbbreviation = (
  value: string | null | undefined,
  timezone?: string | null,
): string => {
  if (!value) {
    return '';
  }

  if (timezone) {
    return moment.tz(value, timezone).format('z');
  }

  return moment(value).format('z');
};

export const formatEventTimezoneDisplay = (
  timezone?: string | null,
  referenceTime?: string | null,
): string => {
  if (!timezone) {
    return '';
  }

  const momentInTz = referenceTime ? moment.tz(referenceTime, timezone) : moment.tz(timezone);
  const abbreviation = momentInTz.format('z');
  const offset = momentInTz.format('Z');

  return `${abbreviation}(UTC ${offset})`;
};
